import { GridHookForm, Button } from 'vnetwork-ui'
import { Contract } from '@/modules/Contract/types'
import PermanentAddress from '@/modules/Employee/components/Confirmation/PermanentAddress'
import { EMPLOYEE_GENDER } from '@/modules/Employee/constants'
import { Path, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { getCustomerById } from '@/modules/Customer/services'
import { useState } from 'react'
import { User } from 'lucide-react'
import dayjs from 'dayjs'

interface AdminInformationProps {
    rootName: string
}

const AdminInformation: React.FC<AdminInformationProps> = ({ rootName }) => {
    const { t } = useTranslation()
    const formControl = useFormContext<Contract>()
    const [isPrefilling, setIsPrefilling] = useState(false)

    const customer_id = useWatch({
        control: formControl.control,
        name: 'customer_id',
    })

    const handlePrefillFromCustomer = async () => {
        if (!customer_id) {
            return
        }

        try {
            setIsPrefilling(true)
            const response = await getCustomerById(customer_id)
            const customer = response?.result

            if (!customer) return

            // Get current form values to copy UI address to admin address
            const currentValues = formControl.getValues()
            const uiFields =
                currentValues?.order?.products?.[0]?.service_domain_metadata
                    ?.domain_extra_fields

            console.log('[AdminInfo] Current form values:', currentValues)
            console.log('[AdminInfo] UI fields:', uiFields)

            // Prefill admin information from customer data
            formControl.setValue(
                `${rootName}.domain_extra_fields.adminName` as Path<Contract>,
                customer.customer_name || '',
                { shouldValidate: true }
            )
            formControl.setValue(
                `${rootName}.domain_extra_fields.adminEmail` as Path<Contract>,
                customer.customer_email || '',
                { shouldValidate: true }
            )
            formControl.setValue(
                `${rootName}.domain_extra_fields.adminPhone` as Path<Contract>,
                customer.customer_phone || '',
                { shouldValidate: true }
            )
            formControl.setValue(
                `${rootName}.domain_extra_fields.adminPosition` as Path<Contract>,
                customer.customer_position || '',
                { shouldValidate: true }
            )

            // Copy address fields from UI (Step 2) to admin (Step 3)
            if (uiFields) {
                console.log('[AdminInfo] Copying address fields from UI...')

                // Copy country
                if (
                    uiFields.uiCountry &&
                    typeof uiFields.uiCountry === 'object'
                ) {
                    console.log(
                        '[AdminInfo] Copying uiCountry:',
                        uiFields.uiCountry
                    )
                    formControl.setValue(
                        `${rootName}.domain_extra_fields.adminCountry` as Path<Contract>,
                        uiFields.uiCountry,
                        { shouldValidate: true, shouldDirty: true }
                    )
                } else {
                    console.warn(
                        '[AdminInfo] uiCountry not found or invalid:',
                        uiFields.uiCountry
                    )
                }

                // Copy province
                if (
                    uiFields.uiProvince &&
                    typeof uiFields.uiProvince === 'object'
                ) {
                    console.log(
                        '[AdminInfo] Copying uiProvince:',
                        uiFields.uiProvince
                    )
                    formControl.setValue(
                        `${rootName}.domain_extra_fields.adminProvince` as Path<Contract>,
                        uiFields.uiProvince,
                        { shouldValidate: true, shouldDirty: true }
                    )
                } else {
                    console.warn('[AdminInfo] uiProvince not found or invalid')
                }

                // Copy district
                if (
                    uiFields.uiDistrict &&
                    typeof uiFields.uiDistrict === 'object'
                ) {
                    console.log(
                        '[AdminInfo] Copying uiDistrict:',
                        uiFields.uiDistrict
                    )
                    formControl.setValue(
                        `${rootName}.domain_extra_fields.adminDistrict` as Path<Contract>,
                        uiFields.uiDistrict,
                        { shouldValidate: true, shouldDirty: true }
                    )
                }

                // Copy ward
                if (uiFields.uiWard && typeof uiFields.uiWard === 'object') {
                    console.log('[AdminInfo] Copying uiWard:', uiFields.uiWard)
                    formControl.setValue(
                        `${rootName}.domain_extra_fields.adminWard` as Path<Contract>,
                        uiFields.uiWard,
                        { shouldValidate: true, shouldDirty: true }
                    )
                }

                // Copy address
                if (uiFields.uiAddress) {
                    console.log(
                        '[AdminInfo] Copying uiAddress:',
                        uiFields.uiAddress
                    )
                    formControl.setValue(
                        `${rootName}.domain_extra_fields.adminAddress` as Path<Contract>,
                        uiFields.uiAddress,
                        { shouldValidate: true, shouldDirty: true }
                    )
                }
            } else {
                console.warn(
                    '[AdminInfo] UI fields not found! Please fill Step 2 first.'
                )
            }

            // Prefill company name if exists
            if (customer.company) {
                formControl.setValue(
                    `${rootName}.domain_extra_fields.adminCompany` as Path<Contract>,
                    customer.company.company_name || '',
                    { shouldValidate: true }
                )
            }

            // Prefill birthdate if available
            if (customer.customer_dob) {
                formControl.setValue(
                    `${rootName}.domain_extra_fields.adminBirthdate` as Path<Contract>,
                    dayjs(customer.customer_dob),
                    { shouldValidate: true }
                )
            }
        } catch (error) {
            console.error('Failed to prefill admin info:', error)
        } finally {
            setIsPrefilling(false)
        }
    }

    return (
        <div data-tour="vnnic-admin">
            <div className="flex items-center justify-between mb-2">
                <Typogaphy className="font-medium" size="medium">
                    {t('admin-information')}
                </Typogaphy>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handlePrefillFromCustomer}
                    disabled={!customer_id || isPrefilling}
                    loading={isPrefilling}
                    startIcon={<User size={16} />}
                >
                    {t('copy-from-declares-info')}
                </Button>
            </div>

            <GridHookForm
                formControl={formControl}
                actions={[]}
                data={[
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.adminName` as Path<Contract>,
                            type: 'textfield',
                            label: t('adminName'),
                            placeholder: t('adminName'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.adminID_Number` as Path<Contract>,
                            type: 'textfield',
                            label: t('adminID_Number'),
                            placeholder: t('adminID_Number'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        renderEditComponent: () => {
                            return (
                                <PermanentAddress
                                    // @ts-ignore
                                    city_key={`${rootName}.domain_extra_fields.adminProvince`}
                                    // @ts-ignore
                                    district_key={`${rootName}.domain_extra_fields.adminDistrict`}
                                    // @ts-ignore
                                    ward_key={`${rootName}.domain_extra_fields.adminWard`}
                                    // @ts-ignore
                                    steet_key={`${rootName}.domain_extra_fields.adminAddress`}
                                    // @ts-ignore
                                    country_key={`${rootName}.domain_extra_fields.adminCountry`}
                                    padding={false}
                                    returnFullOption
                                    renderLabel={(value: any) => {
                                        return value?.name || value?.label
                                    }}
                                    valueMapper={(value: any) => {
                                        if (
                                            !value ||
                                            !value?.label ||
                                            !value?.value
                                        )
                                            throw new Error(
                                                'Something went wrong'
                                            )

                                        return {
                                            label: value?.label,
                                            value: value?.value,
                                        }
                                    }}
                                />
                            )
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.adminEmail` as Path<Contract>,
                            type: 'textfield',
                            label: t('adminEmail'),
                            placeholder: t('adminEmail'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.adminPhone` as Path<Contract>,
                            type: 'textfield',
                            label: t('adminPhone'),
                            placeholder: t('adminPhone'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.adminGender` as Path<Contract>,
                            type: 'select',
                            label: t('adminGender'),
                            placeholder: t('adminGender'),
                            options: EMPLOYEE_GENDER(t),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.adminFax` as Path<Contract>,
                            type: 'textfield',
                            label: t('adminFax'),
                            placeholder: t('adminFax'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.adminBirthdate` as Path<Contract>,
                            type: 'datepicker',
                            label: t('adminBirthdate'),
                            placeholder: t('adminBirthdate'),
                            defaultValue: null,
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.adminCompany` as Path<Contract>,
                            type: 'textfield',
                            label: t('adminCompany'),
                            placeholder: t('adminCompany'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: `${rootName}.domain_extra_fields.adminPosition` as Path<Contract>,
                            type: 'textfield',
                            label: t('adminPosition'),
                            placeholder: t('adminPosition'),
                        },
                        className: 'col-span-2',
                    },
                ]}
                subForm
            />
        </div>
    )
}

export default AdminInformation
